import { AxiosError, AxiosResponse } from "axios";
import {
  API_URL_GET_CAN_EDIT_INTERNAL_USERS,
  API_URL_USER_PROFILE
} from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { makeQueryString } from "core/makeQueryString";
import { usePagination } from "hooks/table-hooks/usePagination";
import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import { IApiError } from "interfaces/api-error";
import {
  IPostUser,
  IUserRow,
  IUsersResponse,
  User,
  UserType
} from "interfaces/user";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { styled } from "@mui/material/styles";

import { Box, Button, MenuItem, Paper, TextField } from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CreateUserDialog } from "components/Users/CreateUserDialog/CreateUserDialog";
import { UsersTable } from "./UsersTable/UsersTable";
import { SearchInput } from "components/SearchInput/SearchInput";
import { SEARCH_DELAY_MS } from "constants/general";

const PAGE_TITLE = "Manage Users";

const defaultOrdering = "first_name,last_name,email";

export function UserManagementPage(): JSX.Element {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { axios } = useContext(AxiosContext)!;

  const queryClient = useQueryClient();

  const {
    pageIndex,
    pageSize,
    changePageIndex,
    changePageSize
  } = usePagination();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("All");
  const [userStatusFilter, setUserStatusFilter] = useState("active");
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [orderingGetUsers, setOrderingGetUsers] = useState<string[]>([]);

  const [,] = useDebounce(() => setDebouncedSearch(search), SEARCH_DELAY_MS, [
    search
  ]);

  const usersQueryString = useMemo(
    () =>
      makeQueryString({
        page: pageIndex + 1,
        size: pageSize,
        ordering:
          orderingGetUsers.length > 0
            ? orderingGetUsers.join(",")
            : defaultOrdering,
        is_active: userStatusFilter === "deactivated" ? "false" : "true",
        ...(debouncedSearch ? { search: debouncedSearch } : undefined),
        ...(userTypeFilter && userTypeFilter.toLowerCase() !== "all"
          ? { user_type: userTypeFilter }
          : undefined)
      }),
    [
      pageIndex,
      debouncedSearch,
      orderingGetUsers,
      pageSize,
      userStatusFilter,
      userTypeFilter
    ]
  );

  const handleAxiosError = (defaultErrorMessage: string) => (
    err: AxiosError<IApiError>
  ) => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const { data: canEditInternalUsers } = useQuery<boolean>({
    queryKey: ["can-edit-internal-users"],
    queryFn: () =>
      axios
        .get<boolean>(API_URL_GET_CAN_EDIT_INTERNAL_USERS)
        .then(r => r?.data || false),
    refetchOnWindowFocus: false
  });

  // GET: users/
  const fetchUsers = useQuery({
    queryKey: ["users", usersQueryString],
    queryFn: () =>
      axios
        .get<IUsersResponse>(`${API_URL_USER_PROFILE}${usersQueryString}`)
        .then(r => r.data),
    onError: handleAxiosError("Error fetching users."),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  // POST: users/
  const postCreateUser = useMutation(["create-user"], {
    mutationFn: (newUser: IPostUser) =>
      axios
        .post<IPostUser, AxiosResponse<User>>(API_URL_USER_PROFILE, newUser)
        .then(r => r.data),
    onSuccess: (r: User | IApiError) => {
      if (Object.hasOwn(r, "error")) {
        addSnackbarToQueue({
          severity: "error",
          message: (r as IApiError)?.error || "Error creating user."
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["users"] });
      }
    },
    onError: handleAxiosError("Error creating user.")
  });

  const userRows: IUserRow[] = useMemo(
    () =>
      (fetchUsers?.data?.results || []).map(
        (u): IUserRow => ({
          id: u.id,
          firstName: u.first_name,
          lastName: u.last_name,
          email: u.email,
          userType: u.user_type || UserType.INTERNAL,
          isEditable:
            u.user_type === UserType.EXTERNAL ||
            (u.user_type === UserType.INTERNAL && !!canEditInternalUsers),
          isOnyxUser: u?.is_onyx || false,
          role: u.is_admin ? "Admin" : "User",
          isActive: u.is_active || false,
          isAdmin: u.is_admin || false,
          lastLogin: u.last_login
        })
      ),
    [fetchUsers.data, canEditInternalUsers]
  );

  useEffect(() => {
    if (fetchUsers.isFetched) {
      const visibleSelectedUserIds = selectedUserIds.filter(id =>
        fetchUsers?.data?.results?.map(u => u.id).includes(id)
      );
      // Clear out selected user IDs if those user rows are no longer visible in current pagination results.
      const areSameUsers =
        visibleSelectedUserIds.length === selectedUserIds.length &&
        visibleSelectedUserIds.every(uid => selectedUserIds.includes(uid));
      if (!areSameUsers) {
        setSelectedUserIds(visibleSelectedUserIds);
      }
    }
  }, [fetchUsers?.data?.results, fetchUsers.isFetched, selectedUserIds]);

  const loading = fetchUsers.isLoading || postCreateUser.isLoading;

  return (
    <StyledPaper>
      <TableToolbar>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={() => setShowCreateUserDialog(true)}
          startIcon={<AddCircle />}
        >
          Add New User
        </StyledButton>

        <CreateUserDialog
          isOpen={showCreateUserDialog}
          onClose={() => setShowCreateUserDialog(false)}
          onConfirm={async (newUser, resetDialog) => {
            setSelectedUserIds([]);
            setShowCreateUserDialog(false);
            resetDialog();
            await postCreateUser.mutateAsync(newUser);
            addSnackbarToQueue({
              message: "New user was successfully created.",
              severity: "success"
            });
          }}
          canCreateInternalUser={canEditInternalUsers || false}
        />

        <FiltersWrapper>
          <StyledSelect
            select
            size="small"
            variant="outlined"
            name="filterUserStatus"
            label="User Status"
            value={userStatusFilter}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              changePageIndex(0);
              setUserStatusFilter(event.target.value);
            }}
          >
            {["active", "deactivated"].map(optionVal => (
              <MenuItem key={optionVal} value={optionVal}>
                {optionVal.replace(
                  /\b\w/g,
                  match => match.toUpperCase() /* Capitalize option text. */
                )}
              </MenuItem>
            ))}
          </StyledSelect>
          <StyledSelect
            select
            size="small"
            variant="outlined"
            name="filterUserType"
            label="User Type"
            value={userTypeFilter}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              changePageIndex(0);
              setUserTypeFilter(event.target.value);
            }}
          >
            {["All", UserType.INTERNAL, UserType.EXTERNAL].map(optionVal => (
              <MenuItem key={optionVal} value={optionVal}>
                {optionVal.replace(/\b\w/g, match => match.toUpperCase())}
              </MenuItem>
            ))}
          </StyledSelect>
          <SearchInput
            dataQa="UP-Search-Users"
            value={search}
            onChange={newSearchText => {
              changePageIndex(0);
              setSearch(newSearchText);
            }}
            onClear={() => {
              changePageIndex(0);
              setSearch("");
            }}
          />
        </FiltersWrapper>
      </TableToolbar>
      <Paper>
        <UsersTable
          isLoading={loading}
          rows={userRows}
          totalRowCount={fetchUsers?.data?.count || 0}
          currentPage={(fetchUsers?.data?.count || 0) > 0 ? pageIndex : 0}
          onPageChange={p => changePageIndex(p)}
          onPageSizeChange={ps => changePageSize(ps)}
          pageSize={pageSize}
          onRowSelectionChange={selectedRowsIds =>
            setSelectedUserIds(selectedRowsIds)
          }
          onChangeSortBy={sorting =>
            setOrderingGetUsers(sorting.map(s => (s.desc ? "-" : "") + s.id))
          }
        />
      </Paper>
    </StyledPaper>
  );
}

const StyledPaper = styled(Paper)`
  position: relative;
  padding: 16px;
`;
StyledPaper.displayName = "StyledPaper";

const TableToolbar = styled(Box)`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
  font-size: 18px;
  color: #555555;
  margin-bottom: 10px;
  flex-direction: row;
  align-items: center;
`;
TableToolbar.displayName = "TableToolbar";

const FiltersWrapper = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
`;
FiltersWrapper.displayName = "FiltersWrapper";

const StyledButton = styled(Button)`
  flex-shrink: 0;
  max-height: 36px;
  text-wrap: nowrap;
`;
StyledButton.displayName = "StyledButton";

const StyledSelect = styled(TextField)`
  width: 200px;
`;
StyledSelect.displayName = "StyledSelect";
